/* assets/evr-tabs-engine.js
   EVR Tabs Engine (desktop+mobile, indicator glide, mobile infinite duplication supported)
   - Mobile: centered tab is active immediately; commit on scroll-stop
   - Desktop: active by key (all matching tabs)
   - Dispatches "evr:tabs-engine:ready" so inline section scripts can safely mount
*/
(() => {
  if (window.EVR_TabsEngine) return;

  try { if (/[?&]evrtabsdebug=1/.test(location.search)) window.__EVR_TABS_DEBUG = true; } catch (_) {}
  const tlog = (...a) => { if (window.__EVR_TABS_DEBUG) { try { console.log('[EVR-Tabs]', ...a); } catch (_) {} } };

  function clamp(n, a, b) { return Math.max(a, Math.min(b, n)); }

  function mount(userCfg) {
    const cfg = {
      root: null,
      barSelector: '[data-evr-tabsbar]',
      tabSelector: '[data-evr-tab]',
      indicatorSelector: '[data-evr-indicator]',
      isMobileBar: (barEl) => !!barEl.closest('.evr-tabs--mobile'),
      getKey: (tabEl) =>
        tabEl?.getAttribute('data-evr-tab-key') ??
        tabEl?.getAttribute('data-filter') ??
        null,

      centerOnMobileClick: true,

      onCommit: (key, ctx) => {},
      onVisual: (key, ctx) => {},

      initialKey: null,
      commitOnInit: false,     // IMPORTANT default
      commitOnClick: true,
      advanceOnActiveClick: false, // re-clicking the active tab moves to the next tab (kills "dead clicks")
      getNextKey: null,            // optional (currentKey, ctx) => key — overrides default DOM-order cycling
      scrollStopMs: 120,

      prefersReduced: () =>
        window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches,

      preferredTabForKey: null,

      ...userCfg,
    };

    const root = cfg.root;
    if (!root) throw new Error('EVR_TabsEngine.mount: cfg.root is required');

    if (root.__evrTabsEngineInited) return root.__evrTabsEngineApi;
    root.__evrTabsEngineInited = true;

    const prefersReduced = !!cfg.prefersReduced?.();

    const bars = Array.from(root.querySelectorAll(cfg.barSelector)).map((bar) => {
      const isMobile = !!cfg.isMobileBar(bar);
      const indicator = bar.querySelector(cfg.indicatorSelector) || null;
      return { bar, isMobile, indicator, raf: 0, stopTimer: null };
    });

    function tabsIn(barEl) { return Array.from(barEl.querySelectorAll(cfg.tabSelector)); }

    function hasOverflow(barEl) { return (barEl.scrollWidth - barEl.clientWidth) > 2; }

    function centerElementInBar(barEl, el, smooth) {
      if (!barEl || !el) return;
      const left = el.offsetLeft + el.offsetWidth / 2 - barEl.clientWidth / 2;
      const clamped = clamp(left, 0, Math.max(0, barEl.scrollWidth - barEl.clientWidth));
      if (smooth && !prefersReduced) barEl.scrollTo({ left: clamped, behavior: 'smooth' });
      else barEl.scrollLeft = clamped;
    }

    function centeredTab(barEl) {
      const tabs = tabsIn(barEl);
      if (!tabs.length) return null;
      const center = barEl.scrollLeft + barEl.clientWidth / 2;
      let best = null, bestDist = Infinity;
      for (const t of tabs) {
        const c = t.offsetLeft + t.offsetWidth / 2;
        const d = Math.abs(c - center);
        if (d < bestDist) { bestDist = d; best = t; }
      }
      return best;
    }

    function preferredTabForKey(barEl, key) {
      if (typeof cfg.preferredTabForKey === 'function') return cfg.preferredTabForKey(barEl, key);

      const k = String(key);
      const tabs = tabsIn(barEl);
      if (!tabs.length) return null;

      if (cfg.isMobileBar(barEl)) {
        const mid = tabs.find((t) => cfg.getKey(t) === k && t.hasAttribute('data-evr-mid'));
        if (mid) return mid;
      }
      return tabs.find((t) => cfg.getKey(t) === k) || null;
    }

    function moveIndicator(state, activeEl) {
      if (!state.indicator || !activeEl) return;
      state.indicator.style.width = activeEl.offsetWidth + 'px';
      state.indicator.style.transform = `translateX(${activeEl.offsetLeft}px)`;
    }

    function setDesktopActive(key) {
      const desktop = bars.find((b) => !b.isMobile);
      if (!desktop) return;

      const barEl = desktop.bar;
      const tabs = tabsIn(barEl);
      const k = String(key);

      tabs.forEach((t) => {
        const on = cfg.getKey(t) === k;
        t.classList.toggle('is-active', on);
        t.setAttribute('aria-selected', on ? 'true' : 'false');
      });

      moveIndicator(desktop, preferredTabForKey(barEl, key));
    }

    function setMobileVisual(state, activeEl) {
      const tabs = tabsIn(state.bar);
      if (!tabs.length || !activeEl) return;

      tabs.forEach((t) => {
        const on = t === activeEl;
        t.classList.toggle('is-active', on);
        t.setAttribute('aria-selected', on ? 'true' : 'false');
      });

      moveIndicator(state, activeEl);
    }

    function detectInitialKey() {
      if (cfg.initialKey != null) return String(cfg.initialKey);

      const desktop = bars.find((b) => !b.isMobile)?.bar;
      if (desktop) {
        const dt = tabsIn(desktop);
        const k =
          dt.find((t) => t.classList.contains('is-active'))?.getAttribute('data-evr-tab-key') ||
          dt.find((t) => t.getAttribute('aria-selected') === 'true')?.getAttribute('data-evr-tab-key');
        if (k != null) return String(k);
      }

      const any = bars[0]?.bar;
      const first = any ? tabsIn(any)[0] : null;
      return String(cfg.getKey(first) || '0');
    }

    let activeKey = detectInitialKey();

    function syncVisuals() {
      setDesktopActive(activeKey);
      bars.forEach((state) => {
        if (!state.isMobile) return;
        const barEl = state.bar;
        const el = hasOverflow(barEl) ? centeredTab(barEl) : preferredTabForKey(barEl, activeKey);
        if (el) setMobileVisual(state, el);
      });
      cfg.onVisual?.(activeKey, { root, bars, activeKey });
    }

    function commit(key, meta = {}) {
      activeKey = String(key ?? activeKey);
      setDesktopActive(activeKey);

      bars.forEach((state) => {
        if (!state.isMobile) return;
        const barEl = state.bar;
        const el = hasOverflow(barEl) ? centeredTab(barEl) : preferredTabForKey(barEl, activeKey);
        if (el) setMobileVisual(state, el);
      });

      cfg.onCommit?.(activeKey, { root, bars, activeKey, ...meta });
    }

    function orderedKeysForBar(barEl) {
      const seen = new Set();
      const keys = [];
      tabsIn(barEl).forEach((t) => {
        const k = cfg.getKey(t);
        if (k == null) return;
        const ks = String(k);
        if (seen.has(ks)) return;
        seen.add(ks);
        keys.push(ks);
      });
      return keys;
    }

    // Next key after `currentKey`, cycling. Uses the desktop bar order when present
    // (so special mobile-only tabs aren't part of the cycle); section can override.
    function nextKeyAfter(currentKey) {
      if (typeof cfg.getNextKey === 'function') {
        const k = cfg.getNextKey(currentKey, { root, bars });
        return k == null ? null : String(k);
      }
      const barEl = (bars.find((b) => !b.isMobile) || bars[0])?.bar;
      if (!barEl) return null;
      const keys = orderedKeysForBar(barEl);
      if (!keys.length) return null;
      const i = keys.indexOf(String(currentKey));
      if (i === -1) return keys[0];
      return keys[(i + 1) % keys.length];
    }

    // Click (delegated)
    const onClick = (e) => {
      const tab = e.target.closest(cfg.tabSelector);
      if (!tab || !root.contains(tab)) return;

      const key = cfg.getKey(tab);
      if (key == null) return;

      const barEl = tab.closest(cfg.barSelector);
      const state = bars.find((b) => b.bar === barEl);

      // Re-clicking the already-active tab advances to the next tab instead of
      // doing nothing. On mobile the engine's activeKey can momentarily desync
      // from the visually-active (centered) tab — spacer tabs, mid-duplicates,
      // scroll timing — so we ALSO treat a tap on an is-active / aria-selected
      // tab as a re-click, and advance relative to the TAPPED key (not the
      // possibly-stale activeKey).
      let commitKey = String(key);
      const tabIsActive =
        tab.classList.contains('is-active') || tab.getAttribute('aria-selected') === 'true';
      if (cfg.advanceOnActiveClick && (String(key) === String(activeKey) || tabIsActive)) {
        const nk = nextKeyAfter(key);
        if (nk != null && String(nk) !== String(key)) commitKey = nk;
      }
      tlog('click', {
        key: String(key), activeKey: String(activeKey), tabIsActive,
        advance: !!cfg.advanceOnActiveClick, commitKey,
        isMobile: !!(state && state.isMobile),
      });

      // Commit FIRST, then center + set the mobile visual. Otherwise commit()
      // re-derives the mobile visual from centeredTab — which, on an overflowing
      // bar, is still the CURRENT tab because the smooth scroll to the next tab
      // hasn't moved scrollLeft yet — and overrides the advance (re-clicking the
      // active tab on mobile then appears to do nothing).
      if (cfg.commitOnClick) commit(commitKey, { source: 'click', sourceBar: barEl });

      if (state && state.isMobile && cfg.centerOnMobileClick) {
        const centerEl = commitKey === String(key) ? tab : (preferredTabForKey(barEl, commitKey) || tab);
        centerElementInBar(barEl, centerEl, true);
        setMobileVisual(state, centerEl);
      }
    };
    root.addEventListener('click', onClick);

    // Mobile scroll
    bars.forEach((state) => {
      if (!state.isMobile) return;

      state.bar.addEventListener('scroll', () => {
        if (state.raf) cancelAnimationFrame(state.raf);
        state.raf = requestAnimationFrame(() => {
          const c = centeredTab(state.bar);
          if (c) setMobileVisual(state, c);
        });

        if (state.stopTimer) clearTimeout(state.stopTimer);
        state.stopTimer = setTimeout(() => {
          const c = centeredTab(state.bar);
          const k = cfg.getKey(c);
          if (k != null && String(k) !== String(activeKey)) {
            commit(k, { source: 'scroll-stop', sourceBar: state.bar });
          } else {
            setDesktopActive(activeKey);
          }
        }, cfg.scrollStopMs);
      }, { passive: true });
    });

    // Init: center mobile to MID for activeKey, then sync visuals, then commit if requested
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        bars.forEach((state) => {
          if (!state.isMobile) return;
          const target = preferredTabForKey(state.bar, activeKey) || tabsIn(state.bar)[0];
          if (target) centerElementInBar(state.bar, target, false);
        });

        syncVisuals();

        if (cfg.commitOnInit) commit(activeKey, { source: 'init' });
      });
    });

    const api = {
      get activeKey() { return activeKey; },
      setActive(key, opts = {}) {
        commit(key, { source: opts.source || 'api' });
        if (opts.centerMobile) {
          bars.forEach((state) => {
            if (!state.isMobile) return;
            const target = preferredTabForKey(state.bar, key) || tabsIn(state.bar)[0];
            if (target) centerElementInBar(state.bar, target, !!opts.smooth);
          });
        }
      },
      destroy() {
        root.removeEventListener('click', onClick);
        root.__evrTabsEngineInited = false;
      },
    };

    root.__evrTabsEngineApi = api;
    return api;
  }

  window.EVR_TabsEngine = { mount };

  // ✅ This is what your section script expects
  window.dispatchEvent(new CustomEvent('evr:tabs-engine:ready'));
})();
